import React, { useState, useEffect, useContext, useImperativeHandle, forwardRef, useRef } from 'react';
import './ImportEditor.css';
import AppContext from '../../AppContext';
import DOMPurify from 'dompurify';
import { debounce } from 'lodash';

// UTILITY FUNCTIONS
import { organizeRawTextIntoBits } from '../../utils/editor/organizeRawTextIntoBits';
import { replaceDuplicateJokes } from '../../utils/editor/replaceDuplicateJokes';
import { compareTextToKnownJokes } from '../../utils/editor/compareTextToKnownJokes';
import { handleLineClick } from '../../utils/editor/handleLineClick';
import { countAssignedAndUnassignedJokes } from '../../utils/editor/jokeUtils';

// COMPONENTS
import Editor from './Editor';
import DragColumnResizer from '../../utils/editor/DragColumnResizer';

const ImportEditor = forwardRef((props, ref) => {
  const [inputText, setInputText] = useState(''); // Local state to store input in real-time
  const [rawText, setRawText] = useState(''); // Raw text input from the user
  const [organizedRawTextBits, setOrganizedRawTextBits] = useState([]); // Organized bits from raw text
  const [previewBitsArray, setPreviewBitsArray] = useState([]); // Final processed bits for rendering
  const [unfilteredEditorBits, setUnfilteredEditorBits] = useState([]);
  const [editorBits, setEditorBits] = useState([]); // Final bits for the editor
  const [unassignedJokesCount, setUnassignedJokesCount] = useState(0); // Track unassigned jokes
  const previousBitsArray = useRef([]); // Use useRef to avoid re-rendering when this changes

  const { audienceResponseHashTable, setAudienceResponseHashTable } = useContext(AppContext);

  const containerRef = useRef(null); // Reference to the container for calculation purposes
  const [leftColumnWidth, setLeftColumnWidth] = useState(() => {
    if (containerRef.current) {
      return containerRef.current.offsetWidth / 2; // 50% of container width
    }
    return window.innerWidth / 2; // Fallback to window width if container is not available
  });

  // Step 1: Organize the raw text into structured bits   
  useEffect(() => {
    if (rawText) {
      const organizedBits = organizeRawTextIntoBits(rawText);
      setOrganizedRawTextBits(organizedBits);
    }
  }, [rawText]);

  // Step 2: Replace duplicate jokes and update preview bits
  useEffect(() => {
    if (organizedRawTextBits.length > 0) {
      const rawBitsWithStringsAndObjects = replaceDuplicateJokes(organizedRawTextBits, previousBitsArray.current);
      setPreviewBitsArray(rawBitsWithStringsAndObjects);
    }
  }, [organizedRawTextBits]);

  // Step 3: Compare to known jokes and set final processed bits for rendering
  useEffect(() => {
    if (previewBitsArray.length > 0) {
      const bitsToRender = compareTextToKnownJokes(previewBitsArray, audienceResponseHashTable);

      // Only update previousBitsArray if its content actually changes
      if (JSON.stringify(previousBitsArray.current) !== JSON.stringify(bitsToRender)) {
        previousBitsArray.current = bitsToRender; // Update the ref, not the state
      }

      setUnfilteredEditorBits(bitsToRender);

      const { unassignedCount } = countAssignedAndUnassignedJokes(bitsToRender);
      setUnassignedJokesCount(unassignedCount);
    }
  }, [previewBitsArray, audienceResponseHashTable]);

  // Step 4: Prepare array for Editor component by removing non-object entries
  useEffect(() => {
    if (unfilteredEditorBits.length > 0) {
      const filteredBits = unfilteredEditorBits.map(bit => bit.filter(item => typeof item === 'object'));
      setEditorBits(filteredBits);
    }
  }, [unfilteredEditorBits]);

  // Handle text change from the raw text input with debouncing
  const handleTextChange = (e) => {
    const text = e.target.value;
    setInputText(text); // Update input text in real-time
    const sanitizedText = DOMPurify.sanitize(text); // Sanitize the input
    debouncedSetRawText(sanitizedText); // Debounced update to rawText
  };

  // Debounced function to update rawText
  const debouncedSetRawText = debounce((sanitizedText) => {
    setRawText(sanitizedText);
  }, 300);

  // Handle line click event for jokes
  const onLineClick = (bitIndex, jokeIndex, lineIndex) => {
    const updatedBitsArray = handleLineClick(
      bitIndex,
      jokeIndex,
      lineIndex,
      previewBitsArray,
      audienceResponseHashTable,
      setAudienceResponseHashTable
    );

    if (updatedBitsArray) {
      setPreviewBitsArray(updatedBitsArray);
    }
  };

  // Define the getData function to be used when saving
  const getData = () => {
    return {
      performance: {
        performanceNum: 1,
        place: 'Unknown',
        date: new Date().toISOString().slice(0, 10),
        rawText,
        previewBitsArray,
        editorBits,
        audienceResponseHashTable,
      },
    };
  };

  // Expose getData function to parent via ref
  useImperativeHandle(ref, () => ({
    getData,
    importPerformanceData: (importedData) => {
      if (importedData && importedData.performance) {
        setInputText(importedData.performance.rawText);
        setPreviewBitsArray(importedData.performance.previewBitsArray);
        setEditorBits(importedData.performance.editorBits);
        setAudienceResponseHashTable(importedData.performance.audienceResponseHashTable);
        console.log('Performance data imported successfully');
        console.log("importedData: , ", importedData.performance.rawText)
      } else {
        console.error('Invalid performance data format');
      }
    },
  }));

  return (
    <div className="import-editor-container">
      {unassignedJokesCount > 0 && (
        <div className="helpful-container">
          <p className="helpful-text">
            Click the punchline for {unassignedJokesCount} joke(s)
          </p>
        </div>
      )}
  
      <div className="editor-columns-wrapper" ref={containerRef}>
        <div className="import-editor-left-column" style={{ width: `${leftColumnWidth}px` }}>
          <div className="column-header">Raw Text</div>
          <textarea
            className="raw-textarea"
            value={inputText}
            onChange={handleTextChange}
            placeholder="Paste your text here"
          />
        </div>
        <DragColumnResizer
          containerRef={containerRef}
          leftColumnWidth={leftColumnWidth}
          setLeftColumnWidth={setLeftColumnWidth}
        />
        <div className="import-editor-right-column" style={{ width: `calc(100% - 20px - ${leftColumnWidth}px)` }}>
          <div className="column-header">Preview</div>
          <div className="preview-pane">
            {previewBitsArray.length > 0 &&
              previewBitsArray.map((bit, bitIndex) => (
                <div key={`bit-${bitIndex}`} className="preview-bit-container">
                  {bit.map((joke, jokeIndex) => {
                    // Check if the joke is standalone (only one line)
                    const isStandalone =
                      typeof joke === 'string' && joke.split('\n').length === 1;
  
                    return (
                      <div
                        key={`joke-${jokeIndex}`}
                        className={`preview-joke-container-raw ${isStandalone ? 'standalone-joke' : ''}`}
                        title={isStandalone ? 'Jokes must be at least 2 lines' : ''}
                      >
                        {typeof joke === 'string' ? (
                          joke.split('\n').map((line, lineIndex) => (
                            <div
                              key={`line-${lineIndex}`}
                              className={`uncategorized-joke-line ${isStandalone ? 'standalone-line' : ''}`}
                              onClick={() => onLineClick(bitIndex, jokeIndex, lineIndex)}
                            >
                              {line}
                            </div>
                          ))
                        ) : (
                          <>
                            {joke.setup.map((line, lineIndex) => (
                              <div key={`setup-line-${lineIndex}`} className="preview-setup">
                                {line}
                              </div>
                            ))}
                            <div className="preview-punchline">{joke.punchline.text}</div>
                            {joke.taglines.map((tagline, taglineIndex) =>
                              tagline.punchline.text.split('\n').map((line, lineIndex) => (
                                <div key={`tagline-${taglineIndex}-${lineIndex}`} className="preview-tagline">
                                  {line}
                                </div>
                              ))
                            )}
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
          </div>
        </div>
      </div>
  
      {Array.isArray(editorBits) && editorBits.length > 0 && editorBits.some(arr => Array.isArray(arr) && arr.length > 0) ? (
        <Editor bits={editorBits} setBits={setEditorBits} />
      ) : (
        <div className="importEditor-editorBits-empty-container">
          <div className="importEditor-editorBits-empty-text">Add text and assign punchlines to view stats</div>
        </div>
      )}
    </div>
  );
  
  
});

export default ImportEditor;
